import { Button, Collapse, Pagination, Spin, Tabs, Typography } from 'antd'
import {
  AsPipelineStatus,
  AsScanStatusType,
  buildStatusMap,
  entriesToAsPipelineStatus,
  getStatusColor,
} from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import filter from 'lodash/filter'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

import AsSyncStatusSteps from './AsSyncStatusSteps'

const { Panel } = Collapse
const { TabPane } = Tabs
const { Text } = Typography

const STATUS_SECTION_PAGE_SIZE = 10

const StyledFilterButton = styled(Button)<{ statusColor: string }>`
  background-color: ${(props) => props.statusColor};
  color: black;
  flex: 1;
  opacity: 0.8;
  font-size: 0.7rem;
  text-align: center;

  &:hover {
    opacity: 1;
  }
`

interface AsSyncStatusSectionProps {
  inspectionSlug: string
}

export const AsSyncStatusSection: React.FC<AsSyncStatusSectionProps> = ({
  inspectionSlug,
}) => {
  const { entries, isLoading, forceRefresh } = useRolodexEntries(
    inspectionSlug
      ? {
          type: RolodexEntryType.AS_PIPELINE_STATUS,
          tags: { inspection: inspectionSlug },
        }
      : undefined
  )

  const pipelineStatus: AsPipelineStatus | undefined = useMemo(
    () => entriesToAsPipelineStatus(entries),
    [entries]
  )

  const scanLevelStatusMap = useMemo(
    () => buildStatusMap(pipelineStatus),
    [pipelineStatus]
  )

  const [currentPage, setCurrentPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState<AsScanStatusType | null>(
    null
  )

  const filteredScanStatus = useMemo(() => {
    const scanLevelEntries = Object.entries(pipelineStatus?.scan_level || {})
    return filterStatus
      ? scanLevelEntries.filter(
          ([scanId]) => scanLevelStatusMap[scanId] === filterStatus
        )
      : scanLevelEntries
  }, [pipelineStatus, filterStatus, scanLevelStatusMap])

  const paginatedScanStatus = useMemo(
    () =>
      filteredScanStatus.slice(
        (currentPage - 1) * STATUS_SECTION_PAGE_SIZE,
        currentPage * STATUS_SECTION_PAGE_SIZE
      ),
    [currentPage, filteredScanStatus]
  )

  const statusSummary = useMemo(() => {
    return {
      error: filter(scanLevelStatusMap, (status) => status === 'error').length,
      process: filter(scanLevelStatusMap, (status) => status === 'process')
        .length,
      success: filter(scanLevelStatusMap, (status) => status === 'finish')
        .length,
    }
  }, [scanLevelStatusMap])

  const handleFilterStatus = (status: AsScanStatusType) => {
    setFilterStatus((prev) => (prev === status ? null : status))
    setCurrentPage(1)
  }

  return (
    <>
      <Spin spinning={isLoading}>
        <Tabs defaultActiveKey='1'>
          <TabPane tab='Scan Level' key='1'>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <Text>
                  Triggered ID:{' '}
                  {pipelineStatus?.trigger_id.toLocaleString() || 'N/A'}
                </Text>
                <Text>
                  Triggered Date:{' '}
                  {pipelineStatus?.triggered_date.toLocaleString() || 'N/A'}
                </Text>
              </div>
              <Button
                onClick={() => {
                  forceRefresh()
                }}>
                Refresh
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '1rem',
                gap: '0.5rem',
              }}>
              <StyledFilterButton
                statusColor={getStatusColor('finish')}
                onClick={() => handleFilterStatus('finish')}>
                <Text strong style={{ fontSize: '0.7rem' }}>
                  Success ({statusSummary.success})
                </Text>
              </StyledFilterButton>
              <StyledFilterButton
                statusColor={getStatusColor('process')}
                onClick={() => handleFilterStatus('process')}>
                <Text strong style={{ fontSize: '0.7rem' }}>
                  Process ({statusSummary.process})
                </Text>
              </StyledFilterButton>
              <StyledFilterButton
                statusColor={getStatusColor('error')}
                onClick={() => handleFilterStatus('error')}>
                <Text strong style={{ fontSize: '0.7rem' }}>
                  Fail ({statusSummary.error})
                </Text>
              </StyledFilterButton>
            </div>
            {pipelineStatus ? (
              <>
                <Collapse>
                  {paginatedScanStatus.map(([scanId], index) => (
                    <Panel
                      key={index}
                      header={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(
                                scanLevelStatusMap[scanId]
                              ),
                              marginRight: 8,
                            }}
                          />
                          <Text>{`${scanId}`}</Text>
                        </div>
                      }>
                      <AsSyncStatusSteps
                        scanLevelStatus={pipelineStatus.scan_level[scanId]}
                      />
                    </Panel>
                  ))}
                </Collapse>
                <Pagination
                  showSizeChanger={false}
                  pageSize={STATUS_SECTION_PAGE_SIZE}
                  current={currentPage}
                  total={filteredScanStatus.length}
                  onChange={(page) => setCurrentPage(page)}
                  style={{
                    marginTop: '1rem',
                    textAlign: 'center',
                    width: 'fit-content',
                    maxWidth: '100%',
                  }}
                />
              </>
            ) : (
              <Text>
                Pipeline Status is not available yet. Please try triggering the
                pipeline first.
              </Text>
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </>
  )
}
